import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Modal, Badge } from 'react-bootstrap';
import { Plus } from 'react-bootstrap-icons';
import EMMSectionHeader from './EMMSectionHeader';
import { useAccount } from '../contexts/AccountContext';
import { useGetUserPreferencesQuery } from '../services/api';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../utils/tenantTimezone';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';

const EmailTemplates = () => {
  const { selectedAccount, isAllAccountsSelected } = useAccount();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    // TODO: Fetch templates from API - ACCOUNT SPECIFIC (or shared across accounts)
    // For now, templates are shared but we show account badge for context
    // In future, templates could be account-specific if needed
    setTemplates([
      {
        id: 1,
        name: 'Welcome Email',
        subject: 'Welcome to our newsletter!',
        createdAt: '2024-01-10',
        lastUsed: '2024-01-15'
      },
      {
        id: 2,
        name: 'Product Announcement',
        subject: 'New Product Launch - Check it out!',
        createdAt: '2024-01-08',
        lastUsed: null
      },
      {
        id: 3,
        name: 'Newsletter Template',
        subject: 'Weekly Newsletter - {date}',
        createdAt: '2024-01-05',
        lastUsed: '2024-01-12'
      }
    ]);
  }, []);

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowModal(true);
  };

  const formatTemplateDate = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return 'Never';
    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      const localDate = new Date(year, month - 1, day);
      return localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    return (
      toLocaleDateStringInTimezone(
        raw,
        'en-US',
        { month: 'short', day: 'numeric', year: 'numeric' },
        tenantTimezone
      ) || raw
    );
  };

  return (
    <>
      <EMMSectionHeader title="Email Templates" showHelp />
      <Container className="mt-3">
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            <Plus className="me-1" size={14} />
            Template
          </Button>
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>{templates.length} templates</span>
        </div>
      </div>
      <Row>
        <Col>
          {!isAllAccountsSelected && selectedAccount?.alias && (
            <p className="text-muted small mb-0">
              Templates are shared across all accounts. When creating a campaign, it will be associated with {selectedAccount.alias}.
            </p>
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Template Name</th>
                    <th>Subject</th>
                    <th>Created</th>
                    <th>Last Used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.id}>
                      <td>
                        <strong>{template.name}</strong>
                      </td>
                      <td>{template.subject}</td>
                      <td>{formatTemplateDate(template.createdAt)}</td>
                      <td>{formatTemplateDate(template.lastUsed)}</td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="me-2"
                          onClick={() => handleViewTemplate(template)}
                        >
                          View
                        </Button>
                        <Button size="sm" variant="outline-secondary" className="me-2">
                          Edit
                        </Button>
                        <Button size="sm" variant="outline-danger">
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Template Preview Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedTemplate?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTemplate && (
            <div>
              <h6>Subject: {selectedTemplate.subject}</h6>
              <hr />
              <div className="border p-3 bg-light">
                <p>Template content preview would go here...</p>
                <p>This is where the actual email template content would be displayed.</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary">
            Use Template
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default EmailTemplates;

